import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { resolveMediaUrl } from "@/lib/api";
import { askAboutCar } from "@/lib/enquiry";
import { Phone, MessageSquare, Gauge, Fuel, Cog, Calendar, Palette } from "lucide-react";

const FALLBACK =
    "https://images.pexels.com/photos/4480505/pexels-photo-4480505.jpeg?auto=compress&cs=tinysrgb&w=1200";

export default function CarDetailDialog({ car, settings, open, onClose }) {
    const [idx, setIdx] = useState(0);
    if (!car) return null;

    const images = (car.image_urls && car.image_urls.length > 0
        ? car.image_urls
        : [FALLBACK]
    ).map(resolveMediaUrl);

    // Use business phone from settings (or mobile as fallback). Digits-only for tel: link.
    const rawPhone = settings?.phone || settings?.mobile || "";
    const dialHref = rawPhone
        ? `tel:${rawPhone.replace(/[^0-9+]/g, "")}`
        : null;

    const scrollToContact = () => {
        onClose();
        setTimeout(() => askAboutCar(car), 200);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl bg-[#0a0a0a] border border-white/10 text-white p-0 rounded-none overflow-hidden">
                <DialogTitle className="sr-only">
                    {car.make} {car.model} {car.year}
                </DialogTitle>
                <div className="grid md:grid-cols-2">
                    <div className="bg-black">
                        <div className="aspect-[4/3] relative">
                            <img
                                src={images[idx]}
                                alt={`${car.make} ${car.model}`}
                                className="w-full h-full object-cover"
                            />
                            <span className="absolute top-3 left-3 bg-[#d52b1e] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1 font-bold">
                                {car.condition}
                            </span>
                            {car.featured && (
                                <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-black text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 font-bold shadow-lg">
                                    ★ Featured
                                </span>
                            )}
                        </div>
                        {images.length > 1 && (
                            <div className="flex gap-2 p-3 overflow-x-auto">
                                {images.map((src, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setIdx(i)}
                                        className={`w-20 h-14 flex-shrink-0 border ${i === idx ? "border-[#d52b1e]" : "border-white/10"}`}
                                        data-testid={`car-thumb-${i}`}
                                    >
                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="p-6 md:p-8 overflow-y-auto max-h-[70vh]">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                            {car.body_type || "Vehicle"} · {car.year}
                        </div>
                        <h3 className="font-display uppercase text-4xl leading-none mt-2">
                            {car.make}
                        </h3>
                        <h4 className="font-display uppercase text-3xl text-[#d52b1e] leading-none mt-1 mb-6">
                            {car.model}
                        </h4>

                        <div className="grid grid-cols-2 gap-4 text-sm border-t border-white/10 pt-5">
                            <Stat icon={<Calendar />} label="Year" value={car.year} />
                            <Stat
                                icon={<Gauge />}
                                label="Mileage"
                                value={car.mileage ? `${car.mileage.toLocaleString()} km` : "—"}
                            />
                            <Stat icon={<Fuel />} label="Fuel" value={car.fuel} />
                            <Stat icon={<Cog />} label="Transmission" value={car.transmission} />
                            <Stat icon={<Palette />} label="Colour" value={car.color || "—"} />
                            <Stat
                                icon={<Cog />}
                                label="Engine"
                                value={car.engine_size ? `${car.engine_size} L` : "—"}
                            />
                        </div>

                        {car.description && (
                            <div className="mt-6">
                                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                                    Parts & Notes
                                </div>
                                <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">
                                    {car.description}
                                </p>
                            </div>
                        )}

                        {car.vin && (
                            <div className="mt-5 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                VIN: <span className="text-zinc-300">{car.vin}</span>
                            </div>
                        )}

                        <div className="mt-7 flex flex-wrap gap-3">
                            <button
                                onClick={scrollToContact}
                                className="bg-[#d52b1e] hover:bg-[#f03022] text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold inline-flex items-center gap-2"
                                data-testid="car-inquire-btn"
                            >
                                <MessageSquare className="w-4 h-4" /> Inquire about parts
                            </button>
                            <a
                                href={dialHref || "#contact"}
                                onClick={(e) => {
                                    if (!dialHref) {
                                        e.preventDefault();
                                        scrollToContact();
                                    }
                                }}
                                data-testid="car-call-btn"
                                className="border border-white/30 hover:border-white text-white px-5 py-3 uppercase text-xs tracking-[0.25em] font-bold inline-flex items-center gap-2"
                            >
                                <Phone className="w-4 h-4" /> {dialHref ? "Call now" : "Contact us"}
                            </a>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Stat({ icon, label, value }) {
    return (
        <div>
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-[0.25em]">
                <span className="w-3.5 h-3.5 [&_svg]:w-3.5 [&_svg]:h-3.5">{icon}</span>
                {label}
            </div>
            <div className="text-white mt-1">{value}</div>
        </div>
    );
}
